/**
 * frontend/src/modules/referentials/lib/api.ts
 * Appels API — module Référentiels.
 *
 * Endpoints backend :
 *   GET /api/v1/admin/pdvs           (PDVs — module admin/pdvs)
 *   GET /api/v1/admin/pdvs/:id
 *   GET /api/v1/referentials/skus
 *   GET /api/v1/referentials/skus/:id
 *   GET /api/v1/referentials/brands
 *   GET /api/v1/referentials/brands/:id
 *   GET /api/v1/performance/merchandisers   (merchandisers — module performance)
 *   GET /api/v1/performance/merchandisers/:id
 *   GET /api/v1/referentials/wilayas
 *   GET /api/v1/referentials/regions
 *
 * Phase 4.I.8 · DECISIONS #101
 */

import { apiClient } from '@/lib/apiClient';
import type {
  PaginatedResponse,
  PdvListItem,
  PdvFilters,
  SkuListItem,
  SkuFilters,
  BrandListItem,
  MerchandiserListItem,
  MerchandiserFilters,
  WilayaRef,
  RegionRef,
} from '../types/referentials.types';

// ── PDVs ──────────────────────────────────────────────────────────────────────

export async function fetchPdvs(
  filters: PdvFilters = {},
  page = 1,
  perPage = 50,
): Promise<PaginatedResponse<PdvListItem>> {
  const params: Record<string, unknown> = { page, per_page: perPage };
  if (filters.q?.trim())             params['q']                 = filters.q.trim();
  if (filters.channel_type)          params['channel_type']      = filters.channel_type;
  if (filters.market_cluster_id)     params['market_cluster_id'] = filters.market_cluster_id;
  if (filters.segment)               params['segment']           = filters.segment;
  if (filters.wilaya_id)             params['wilaya_id']         = filters.wilaya_id;
  if (filters.is_active !== undefined) params['is_active']       = filters.is_active;
  const res = await apiClient.get<PaginatedResponse<PdvListItem>>('/admin/pdvs', { params });
  return res.data;
}

export interface PdvCounts {
  total: number;
  active: number;
  inactive: number;
}

/** GET /admin/pdvs/counts — compteur triple (HOTFIX-PDV-ACTIVATION) */
export async function fetchPdvCounts(): Promise<PdvCounts> {
  const res = await apiClient.get<PdvCounts>('/admin/pdvs/counts');
  return res.data;
}

export interface StatBucketDto { cle: string; libelle: string; n: number }

export interface PdvStats {
  total:      number;
  active:     number;
  inactive:   number;
  /** Couverture géographique — une présence, pas une répartition. */
  wilayasCouvertes: number;
  wilayasTotal:     number;
  parRegion:  StatBucketDto[];
  parCanal:   StatBucketDto[];
  parSegment: StatBucketDto[];
}

/**
 * GET /admin/pdvs/stats — bandeau de statistiques du référentiel PDV.
 *
 * ⚠ AUCUN PARAMÈTRE, ET C'EST LE CONTRAT : la portée est LE RÉFÉRENTIEL ENTIER.
 *   Passer les filtres ici transformerait le bandeau en résumé de la liste — ce
 *   qui est précisément la lecture qu'on cherche à empêcher.
 * ⚠ CETTE RÉPONSE PORTE DÉJÀ `total` / `active` / `inactive` : la page NE DOIT
 *   PAS appeler `fetchPdvCounts` en plus. Deux endroits affichant le même compte
 *   finissent par diverger, et rien à l'écran ne dit lequel a raison.
 *   `fetchPdvCounts` reste vivant pour `BentoHero`, seul autre appelant.
 */
export async function fetchPdvStats(): Promise<PdvStats> {
  const res = await apiClient.get<PdvStats>('/admin/pdvs/stats');
  return res.data;
}

export interface SkuStats {
  total:      number;
  active:     number;
  inactive:   number;
  parMarque:  StatBucketDto[];
  parSegment: StatBucketDto[];
}

/**
 * GET /referentials/skus/stats — bandeau du référentiel SKU.
 * ⚠ AUCUN PARAMÈTRE : portée = référentiel entier, comme PDV.
 * ⚠ `parSegment` NE VIENT PAS DU RÉFÉRENTIEL mais des observations — la carte
 *   qui l'affiche doit le dire, sinon elle se lit comme ses deux voisines.
 */
export async function fetchSkuStats(): Promise<SkuStats> {
  const res = await apiClient.get<SkuStats>('/referentials/skus/stats');
  return res.data;
}

export interface MerchStats {
  total:     number;
  active:    number;
  inactive:  number;
  parCanal:  StatBucketDto[];
  parRegion: StatBucketDto[];
}

/**
 * GET /referentials/merchandisers/stats — bandeau du référentiel Merchandisers.
 * ⚠ `parRegion` est DÉRIVÉ des tournées côté serveur, avec la MÊME requête que
 *   `/referentials/merchandisers`. Le front n'en calcule aucune part.
 */
export async function fetchMerchStats(): Promise<MerchStats> {
  const res = await apiClient.get<MerchStats>('/referentials/merchandisers/stats');
  return res.data;
}

export interface RoutingStats {
  total:        number;
  pdvsCouverts: number;
  pdvsTotal:    number;
  parJour:      StatBucketDto[];
  parCanal:     StatBucketDto[];
  parRegion:    StatBucketDto[];
}

/**
 * GET /referentials/routings/stats — bandeau du référentiel Routings.
 * ⚠ LE GRAIN EST L'AFFECTATION (1 571), PAS LE ROUTING (27). C'est ce que la
 *   liste affiche ; compter les routings donnerait un total de 27 sous une
 *   liste de 1 571.
 * ⚠ `parCanal` ET `parRegion` SONT EMPRUNTÉS — le canal au merchandiser, la
 *   région au PDV. `routing_visits` ne porte ni l'un ni l'autre : ce sont des
 *   axes de croisement.
 */
export async function fetchRoutingStats(): Promise<RoutingStats> {
  const res = await apiClient.get<RoutingStats>('/referentials/routings/stats');
  return res.data;
}

export async function fetchPdvById(id: string): Promise<PdvListItem> {
  const res = await apiClient.get<PdvListItem>(`/admin/pdvs/${id}`);
  return res.data;
}

// ── SKUs ──────────────────────────────────────────────────────────────────────

export async function fetchSkus(
  filters: SkuFilters = {},
  page = 1,
): Promise<PaginatedResponse<SkuListItem>> {
  const params: Record<string, unknown> = { page, per_page: 20 };
  if (filters.q?.trim())               params['q']                = filters.q.trim();
  if (filters.brand_id)                params['brand_id']         = filters.brand_id;
  if (filters.price_segment_id)        params['price_segment_id'] = filters.price_segment_id;
  if (filters.is_active !== undefined) params['is_active']        = filters.is_active;
  const res = await apiClient.get<PaginatedResponse<SkuListItem>>('/referentials/skus', { params });
  return res.data;
}

export async function fetchSkuById(id: string): Promise<SkuListItem> {
  const res = await apiClient.get<SkuListItem>(`/referentials/skus/${id}`);
  return res.data;
}

// ── Brands ────────────────────────────────────────────────────────────────────

export async function fetchBrandsList(): Promise<BrandListItem[]> {
  const res = await apiClient.get<Array<{
    id: string; code: string; name: string;
    is_client_brand: boolean; brand_group: string | null;
    brand_cluster_default?: string | null;
    color_hex?: string | null; text_color_hex?: string | null;
  }>>('/referentials/brands');
  if (!Array.isArray(res.data)) return [];
  return res.data.map((b) => ({
    id:           b.id,
    code:         b.code,
    name:         b.name,
    isClientBrand: b.is_client_brand,
    /**
     * ⚠ CE CHAMP ÉTAIT SERVI PAR LE SERVEUR ET JETÉ ICI. `listBrands` le rend
     *   depuis toujours et `brandJsonSchema` le déclare — c'est ce mapping qui
     *   ne le recopiait pas, donc le cluster marché était invisible à l'écran
     *   alors que 17 marques sur 19 en portent un.
     */
    brandClusterDefault: b.brand_cluster_default ?? null,
    brandGroup:   b.brand_group ?? null,
    colorHex:     b.color_hex ?? null,
    textColorHex: b.text_color_hex ?? null,
  }));
}

export async function fetchBrandById(id: string): Promise<BrandListItem> {
  const res = await apiClient.get<BrandListItem>(`/referentials/brands/${id}`);
  return res.data;
}

/** PATCH /admin/brands/:id/colors — DECISIONS V3.71 Train 3 J1 */
export async function updateBrandColors(
  id: string,
  colors: { color_hex?: string | null; text_color_hex?: string | null },
): Promise<void> {
  await apiClient.patch(`/admin/brands/${id}/colors`, colors);
}

/** PATCH /admin/brands/:id — champs génériques (is_client_brand, name, …) */
export async function patchBrand(
  id: string,
  body: { is_client_brand?: boolean; name?: string; brand_group?: string | null },
): Promise<void> {
  await apiClient.patch(`/admin/brands/${id}`, body);
}

// ── Merchandisers ─────────────────────────────────────────────────────────────

export async function fetchMerchandisers(
  filters: MerchandiserFilters = {},
  page = 1,
  perPage = 20,
): Promise<PaginatedResponse<MerchandiserListItem>> {
  const params: Record<string, unknown> = { page, per_page: perPage };
  if (filters.q?.trim())    params['q']            = filters.q.trim();
  if (filters.channel_type) params['channel_type'] = filters.channel_type;
  // wilaya_id: non supporté — le modèle Merchandiser n'a pas de wilayaId direct

  const res = await apiClient.get<{
    data: Array<{
      id: string; merchCode: string; channelType: string; isActive: boolean;
      wilayaNames?: string[]; regionNames?: string[];
    }>;
    pagination: { page: number; per_page: number; total: number; total_pages: number };
  }>('/admin/referentials/merchandisers', { params });

  return {
    data: res.data.data.map((m) => ({
      id:          m.id,
      merchCode:   m.merchCode,
      channelType: (m.channelType as MerchandiserListItem['channelType']) ?? null,
      wilayaId:    null,
      /**
       * ⚠ CES DEUX CHAMPS ÉTAIENT `null` EN DUR, ET C'EST POURQUOI LES COLONNES
       *   WILAYA ET RÉGION ÉTAIENT VIDES À L'ÉCRAN sur 27 lignes. Le serveur ne
       *   les rendait pas ; il les rend maintenant, dérivés des tournées.
       * ⚠ CE SONT DES LISTES CÔTÉ SERVEUR — mesuré, 2 merchandisers couvrent
       *   deux wilayas. On les joint pour l'affichage plutôt que d'en choisir
       *   une : choisir en silence serait perdre l'information.
       * ⚠ TABLEAU VIDE ⇒ `null`, pas une chaîne vide. Le tableau rend « — »,
       *   qui dit « pas de tournée » ; une chaîne vide laisserait une case
       *   blanche indistinguable d'un défaut d'affichage.
       */
      wilayaNames: m.wilayaNames ?? [],
      wilayaName:  m.wilayaNames?.length ? m.wilayaNames.join(', ') : null,
      regionName:  m.regionNames?.length ? m.regionNames.join(', ') : null,
      isActive:    m.isActive,
    })),
    pagination: res.data.pagination,
  };
}

export async function fetchMerchandiserById(id: string): Promise<MerchandiserListItem> {
  const res = await apiClient.get<MerchandiserListItem>(`/performance/merchandisers/${id}`);
  return res.data;
}

// ── Lookups ───────────────────────────────────────────────────────────────────

export async function fetchWilayas(): Promise<WilayaRef[]> {
  const res = await apiClient.get<Array<{
    id: string; code: string; name: string;
    region_id: string;
    region: { code: string; name: string };
  }>>('/referentials/wilayas');
  if (!Array.isArray(res.data)) return [];
  return res.data.map((w) => ({
    id:         w.id,
    code:       w.code,
    name:       w.name,
    regionId:   w.region_id,
    regionName: w.region.name,
  }));
}

export async function fetchRegions(): Promise<RegionRef[]> {
  const res = await apiClient.get<RegionRef[]>('/referentials/regions');
  return Array.isArray(res.data) ? res.data : [];
}
